import { Box, Typography, Avatar, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import {
  LocationOnOutlined,
  PersonOutlineOutlined,
  FormatListBulletedOutlined,
  StarOutlineOutlined,
  GroupsOutlined,
  PeopleOutlineOutlined,
  AutoStoriesOutlined,
  VideocamOutlined,
  ArrowDropUp,
  ArrowDropDown,
} from "@mui/icons-material";
import { Button } from "@mui/material";
import { useMutation, useManualQuery } from "graphql-hooks";
import { USER_DATA_BY_UUID } from "../graphQL/user_qry";
import {
  CREATE_FOLLOW_MUTATION,
  DELETE_FOLLOW_MUTATION,
  FOLLOWING_STATUS_BY_USER_FOLLOWER_UUID,
} from "../graphQL/follow_qry";
import { isValidUUIDv4 } from "../hooks/uuid";

import profileBanner from "../assets/PublicPages/profile-banner.png";
import profileImage from "../assets/PublicPages/profile-image.png";
import FollowButton from "../components/FollowButton";
import AlertPopup from "../components/AlertPopup";

// Styled Components
const PageContainer = styled(Box)({
  backgroundColor: "#f5f5f5",
});

const ProfileBanner = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "300px",
  overflow: "hidden",
  backgroundSize: "cover",
  backgroundPosition: "center",
  borderRadius: "12px",
  marginBottom: "16px",
  [theme.breakpoints.down(1360)]: {
    height: "250px",
  },
  [theme.breakpoints.down(833)]: {
    height: "203px",
  },
}));

const MainContainer = styled(Box)(({ theme }) => ({
  margin: "0 auto",
  padding: "24px 100px",
  [theme.breakpoints.down(1360)]: {
    padding: "24px 16px",
  },
}));

const ProfileInfo = styled(Box)({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  color: "white",
  background: "linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent)",
});

const ProfileInfoContent = styled(Box)({
  padding: "0px 20px 30px",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
});

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 96,
  height: 96,
  [theme.breakpoints.down(833)]: {
    width: 60,
    height: 60,
  },
  border: "3px solid white",
  marginRight: "20px",
}));

const UserDetails = styled(Box)({
  flex: 1,
});

const UserStats = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  [theme.breakpoints.down(833)]: {
    display: "none",
  },
}));

const StatDivider = styled(Box)({
  width: "1px",
  height: "40px",
  backgroundColor: "white",
  opacity: 0.3,
  marginRight: "20px",
});

const MobileStatDivider = styled(Box)({
  width: "1px",
  height: "40px",
  marginRight: "25px",
  backgroundColor: "#e0e0e0",
  opacity: 0.6,
});

const StatItem = styled(Box)({
  textAlign: "center",
  paddingRight: "30px",
});

const ContentWrapper = styled(Box)({
  display: "flex",
  gap: "20px",
});

const Sidebar = styled(Box)(({ theme }) => ({
  width: "230px",
  background: "white",
  borderRadius: "8px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  height: "fit-content",
  overflow: "hidden",
  [theme.breakpoints.down(1360)]: {
    display: "none",
  },
}));

const SidebarHeader = styled(Box)({
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  borderBottom: "1px solid #eee",
});

const SidebarNav = styled(Box)({
  padding: "0px",
});

const NavItem = styled(Link)(({ active }) => ({
  display: "flex",
  alignItems: "center",
  padding: "17px 15px",
  textDecoration: "none",
  color: active ? "#e53e3e" : "#666",
  transition: "all 0.2s ease",
  borderLeft: `3px solid ${active ? "#e53e3e" : "transparent"}`,
  backgroundColor: active ? "#fff5f5" : "transparent",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: active ? "#fff5f5" : "#f8f9fa",
    color: active ? "#e53e3e" : "#333",
  },
}));

const NavIcon = styled("span")({
  marginRight: "12px",
  fontSize: "16px",
  display: "flex",
  alignItems: "center",
});

const NavLabel = styled("span")({
  flex: 1,
  fontSize: "14px",
});

const NavCount = styled("span")(({ active }) => ({
  fontSize: "14px",
  color: active ? "#e53e3e" : "#999",
  fontWeight: "normal",
}));

const MainContent = styled(Box)({
  flex: 1,
  background: "white",
  borderRadius: "8px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  minHeight: "500px",
});

const MobileStatsContainer = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down(833)]: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "20px",
    padding: "16px",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
}));

const MobileDropdownContainer = styled(Box)(({ theme }) => ({
  display: "none",
  marginBottom: "20px",
  [theme.breakpoints.down(1360)]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const FollowButtonContainer = styled(Box)(({ theme }) => ({
  paddingTop: "20px",
}));

const PublicPageLayout = ({ 
  children, 
  activeMenuItem, 
  userUuid,
  isFollowing: controlledIsFollowing,
  onFollowChange,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [internalIsFollowing, setInternalIsFollowing] = useState(false);

  const isFollowing =
    controlledIsFollowing !== undefined
      ? controlledIsFollowing
      : internalIsFollowing;

  const setIsFollowing =
    onFollowChange || setInternalIsFollowing;  
  const [usrDataObj, setUsrDataObj] = useState({});
  const [unfollowPopup, setUnfollowPopup] = useState({
    open: false,
    followingName: "",
  });
  const [isInvalidURL, setIsInvalidURL] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [getUsrData] = useManualQuery(USER_DATA_BY_UUID);
  const [getFollowStat] = useManualQuery(
    FOLLOWING_STATUS_BY_USER_FOLLOWER_UUID,
    {
      fetchPolicy: "network-only",
    }
  );
  const [createFollow] = useMutation(CREATE_FOLLOW_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [unFollow] = useMutation(DELETE_FOLLOW_MUTATION, {
    awaitRefetchQueries: true,
  });

  let loggedUserUuid = localStorage.getItem("userUuid")
    ? localStorage.getItem("userUuid")
    : null;

  useEffect(() => {
    (async () => setUserData())();
  }, [userUuid]);

  const setUserData = async () => {
    if (!userUuid) {
      setIsInvalidURL(true);
      return;
    }
    if (isValidUUIDv4(userUuid)) {
      const {
        loading: loadingUsrData,
        error: errorCurrUsrData,
        data: usrData,
      } = await getUsrData({
        variables: {
          uuid: userUuid,
        },
      });
      if (!loadingUsrData && usrData && usrData.userDataByUuid) {
        console.log("usrData:", usrData.userDataByUuid);
        setUsrDataObj(usrData.userDataByUuid);
      } else setIsInvalidURL(true);
      if (errorCurrUsrData) {
        console.log("errorCurrUsrData: ", errorCurrUsrData);
        setIsInvalidURL(true);
      }
    } else setIsInvalidURL(true);
  };

  useEffect(() => {
    (async () => getUserFollowStatus())();
  }, [userUuid, loggedUserUuid]);

  const getUserFollowStatus = async () => {
    if (!userUuid || !loggedUserUuid) return;
    const {
      loading: loadingFollowData,
      error: errorFollowData,
      data: followData,
    } = await getFollowStat({
      variables: {
        userUuid: userUuid,
        followerUuid: loggedUserUuid,
      },
    });
    if (!loadingFollowData && followData) {
      console.log(
        "followData:",
        followData.followingStatusByUseruuidFolloweruuid
      );
      setIsFollowing(followData.followingStatusByUseruuidFolloweruuid.response);
    } else {
      console.log("errorFollowData: ", errorFollowData);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleFollowToggle = async () => {
    if (isFollowing) {
      // Show unfollow confirmation popup
      setUnfollowPopup({
        open: true,
        followingName: usrDataObj.firstName + " " + usrDataObj.lastName,
      });
    } else {
      //save follow to database
      const { data, error } = await createFollow({
        variables: {
          userUuid: userUuid,
        },
      });

      if (error) {
        console.log("Error", error, data);
      } else {
        console.log("Success", data);
        // Direct follow action
        setIsFollowing(prev => !prev);
      }
    }
  };

  const handleConfirmUnfollow = async () => {
    //save unfollow to database
    const { data, error } = await unFollow({
      variables: {
        userUuid: userUuid,
      },
    });

    if (error) {
      console.log("Error", error, data);
    } else {
      console.log("Success", data);
      setIsFollowing(prev => !prev);
      setUnfollowPopup({ open: false, followingName: "" });
    }
  };

  const handleCancelUnfollow = () => {
    setUnfollowPopup({ open: false, followingName: "" });
  };

  const menuItems = [
    {
      id: "profile",
      label: "Profile",
      icon: PersonOutlineOutlined,
      count: null,
      path: `/user/${userUuid}/profile`,
    },
    {
      id: "listings",
      label: "Listings",
      icon: FormatListBulletedOutlined,
      count: usrDataObj && usrDataObj.listings ? usrDataObj.listings : "",
      path: `/user/${userUuid}/listings`,
    },
    {
      id: "ratings",
      label: "Ratings",
      icon: StarOutlineOutlined,
      count: usrDataObj && usrDataObj.ratingCount ? usrDataObj.ratingCount : "",
      path: `/user/${userUuid}/ratings`,
    },
    {
      id: "followers",
      label: "Followers",
      icon: GroupsOutlined,
      count: usrDataObj && usrDataObj.followerNo ? usrDataObj.followerNo : "",
      path: `/user/${userUuid}/followers`,
    },
    {
      id: "following",
      label: "Following",
      icon: PeopleOutlineOutlined,
      count: usrDataObj && usrDataObj.followingNo ? usrDataObj.followingNo : "",
      path: `/user/${userUuid}/followings`,
    },
    {
      id: "stories",
      label: "Stories",
      icon: AutoStoriesOutlined,
      count: usrDataObj && usrDataObj.storiesNo ? usrDataObj.storiesNo : "",
      path: `/user/${userUuid}/stories`,
    },
    {
      id: "videos",
      label: "Videos",
      icon: VideocamOutlined,
      count: usrDataObj && usrDataObj.videosNo ? usrDataObj.videosNo : "",
      path: `/user/${userUuid}/videos`,
    },
  ];

  return (
    <>
      {Object.keys(usrDataObj).length > 0 ? (
        <PageContainer>
          {/* Header with Profile Banner */}
          <MainContainer>
            <ProfileBanner
              sx={{
                backgroundImage: `url(${usrDataObj.coverImage})`,
              }}
            >
              <ProfileInfo>
                <ProfileInfoContent>
                  <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                    <ProfileAvatar
                      src={usrDataObj.avatarFileName}
                      alt="Profile"
                    />
                    <UserDetails>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: "bold",
                          mb: 0.5,
                          "@media (max-width: 833px)": {
                            fontSize: "16px",
                          },
                        }}
                      >
                        {usrDataObj.firstName + " " + usrDataObj.lastName}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          "@media (max-width: 833px)": {
                            fontSize: "12px",
                          },
                        }}
                      >
                        @{usrDataObj.username}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <LocationOnOutlined sx={{ fontSize: 16 }} />
                        <Typography variant="caption">
                          {usrDataObj.city + ", " + usrDataObj.state}
                        </Typography>
                      </Box>
                    </UserDetails>
                  </Box>
                  <UserStats>
                    <StatItem sx={{ paddingRight: "20px" }}>
                      <Typography variant="h6" sx={{ fontWeight: "semibold" }}>
                        {usrDataObj.listings}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Listings
                      </Typography>
                    </StatItem>
                    <StatDivider />
                    <StatItem>
                      <Typography variant="h6" sx={{ fontWeight: "semibold" }}>
                        {Number(usrDataObj.followerNo).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Followers
                      </Typography>
                    </StatItem>
                    <StatItem>
                      <Typography variant="h6" sx={{ fontWeight: "semibold" }}>
                        {Number(usrDataObj.followingNo).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Following
                      </Typography>
                    </StatItem>
                  </UserStats>
                </ProfileInfoContent>
              </ProfileInfo>
            </ProfileBanner>

            {/* Mobile Stats - Only visible on mobile (833px and below) */}
            <MobileStatsContainer>
              <StatItem sx={{ paddingLeft: "18px" }}>
                <Typography variant="h6" sx={{ fontWeight: "semibold" }}>
                  {usrDataObj.listings}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Listings
                </Typography>
              </StatItem>
              <MobileStatDivider />
              <StatItem>
                <Typography variant="h6" sx={{ fontWeight: "semibold" }}>
                  {Number(usrDataObj.followerNo).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Followers
                </Typography>
              </StatItem>
              <MobileStatDivider />
              <StatItem>
                <Typography variant="h6" sx={{ fontWeight: "semibold" }}>
                  {Number(usrDataObj.followingNo).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Following
                </Typography>
              </StatItem>
            </MobileStatsContainer>

            {/* Mobile Dropdown Menu */}
            <MobileDropdownContainer>
              <Box
                ref={dropdownRef}
                sx={{ width: "200px", position: "relative" }}
              >
                <TextField
                  label="Pages"
                  value={(() => {
                    const activeItem = menuItems.find(
                      (item) => item.id === activeMenuItem
                    );
                    return activeItem ? activeItem.label : "";
                  })()}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {dropdownOpen ? (
                          <ArrowDropUp sx={{ color: "#666" }} />
                        ) : (
                          <ArrowDropDown sx={{ color: "#666" }} />
                        )}
                      </Box>
                    ),
                  }}
                  sx={{
                    width: "200px",
                    cursor: "pointer",
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                      backgroundColor: "white",
                      cursor: "pointer",
                      paddingRight: "8px",
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                        borderWidth: "1px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#666",
                      fontSize: "14px",
                      "&.Mui-focused": {
                        color: "#666",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "& .MuiOutlinedInput-input": {
                      cursor: "pointer",
                    },
                  }}
                />

                {/* Dropdown options panel */}
                {dropdownOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "40px",
                      left: 0,
                      right: 0,
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderTop: "none",
                      borderRadius: "0 0 4px 4px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      zIndex: 1000,
                      maxHeight: "400px",
                      overflowY: "auto",
                    }}
                  >
                    {menuItems.map((item) => {
                      const IconComponent = item.icon;
                      return (
                        <Box
                          key={item.id}
                          onClick={() => {
                            navigate(item.path);
                            setDropdownOpen(false);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px 16px",
                            cursor: "pointer",
                            backgroundColor:
                              activeMenuItem === item.id
                                ? "#fff5f5"
                                : "transparent",
                            color:
                              activeMenuItem === item.id ? "#e53e3e" : "#666",
                            "&:hover": {
                              backgroundColor: "#f8f9fa",
                            },
                          }}
                        >
                          <IconComponent sx={{ fontSize: 18 }} />
                          <span style={{ flex: 1 }}>{item.label}</span>
                          {item.count && (
                            <span style={{ color: "#999", fontSize: "14px" }}>
                              {item.count}
                            </span>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              {/* Follow Button - Tablet and Mobile */}
              <FollowButton
                isFollowing={isFollowing}
                onToggle={handleFollowToggle}
              />
            </MobileDropdownContainer>

            <ContentWrapper>
              {/* Left Sidebar Menu */}
              <Sidebar>
                <SidebarHeader>
                  <Typography variant="h6" sx={{ margin: 0, color: "#333" }}>
                    User Overview
                  </Typography>
                  {/* Follow Button - Desktop */}
                  {loggedUserUuid !== userUuid && (
                    <FollowButtonContainer>
                      <FollowButton
                        isFollowing={isFollowing}
                        onToggle={handleFollowToggle}
                      />
                    </FollowButtonContainer>
                  )}
                </SidebarHeader>

                <SidebarNav>
                  {menuItems.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <NavItem
                        key={item.id}
                        active={activeMenuItem === item.id}
                        to={item.path}
                      >
                        <NavIcon>
                          <IconComponent />
                        </NavIcon>
                        <NavLabel>{item.label}</NavLabel>
                        {item.count && (
                          <NavCount active={activeMenuItem === item.id}>
                            {item.count}
                          </NavCount>
                        )}
                      </NavItem>
                    );
                  })}
                </SidebarNav>
              </Sidebar>

              {/* Main Content */}
              <MainContent>
                {typeof children === "function"
                  ? children({ isFollowing })
                  : children}
             </MainContent>
            </ContentWrapper>
          </MainContainer>

          {/* Unfollow Confirmation Popup */}
          <AlertPopup
            open={unfollowPopup.open}
            title="Unfollow This Person"
            message={
              <>
                Are you sure you want to unfollow user{" "}
                <strong>{unfollowPopup.followingName}</strong>?
              </>
            }
            confirmText="YES, UNFOLLOW"
            cancelText="CANCEL"
            onConfirm={handleConfirmUnfollow}
            onCancel={handleCancelUnfollow}
            onClose={handleCancelUnfollow}
            showCheckCircle={true}
          />
        </PageContainer>
      ) : (
        isInvalidURL && (
          <Box sx={{ textAlign: "center", mb: 4, mt: 10 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: "30px",
                "@media (max-width: 856px)": {
                  fontSize: "24px",
                },
                fontWeight: "700",
                color: "#333",
                mb: 1,
              }}
            >
              This URL is not valid.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/dashboard")}
              sx={{
                height: "42px",
                px: 3,
                mt: 5,
                fontWeight: 500,
                backgroundColor: "#EF5658",
                "@media (max-width: 856px)": {
                  fontSize: "13px",
                  height: "30px",
                  px: 1,
                },
              }}
            >
              OK
            </Button>
          </Box>
        )
      )}
    </>
  );
};

export default PublicPageLayout;
